/**
 * Auth endpoint contract.
 *
 * These routes are not implemented by the backend yet (P0.2 shipped JWT
 * sign/verify and the auth middleware, but no `/auth/*` router). The client is
 * defined here so the UI can be built against a fixed contract; every call
 * fails cleanly until the endpoints exist.
 */
import { apiConfig } from '@/config'

import type { SessionUser } from './session-manager'

export interface LoginCredentials {
  email: string
  password: string
}

export interface LoginResponse {
  user: SessionUser
  accessToken: string
  /** Access token lifetime in seconds. */
  expiresIn: number
}

export class AuthApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'AuthApiError'
    this.status = status
  }
}

function requireBaseUrl(): string {
  if (!apiConfig.baseUrl) {
    throw new AuthApiError('VITE_API_URL is not configured', 0)
  }
  return apiConfig.baseUrl
}

async function parseError(response: Response): Promise<never> {
  let message = `Request failed with status ${response.status}`

  try {
    const body = (await response.json()) as { message?: string }
    if (body.message) message = body.message
  } catch {
    // Non-JSON error body; the status-based message stands.
  }

  throw new AuthApiError(message, response.status)
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetch(`${requireBaseUrl()}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: apiConfig.defaultHeaders,
      body: JSON.stringify(credentials),
    })

    if (!response.ok) await parseError(response)
    return (await response.json()) as LoginResponse
  },

  async logout(): Promise<void> {
    const response = await fetch(`${requireBaseUrl()}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: apiConfig.defaultHeaders,
    })

    if (!response.ok) await parseError(response)
  },

  async me(accessToken: string): Promise<SessionUser> {
    const response = await fetch(`${requireBaseUrl()}/auth/me`, {
      credentials: 'include',
      headers: { ...apiConfig.defaultHeaders, Authorization: `Bearer ${accessToken}` },
    })

    if (!response.ok) await parseError(response)
    return (await response.json()) as SessionUser
  },
}
